/**
 * Wishlist page module — /pages/wishlist
 *
 * Renders the grouped wishlist: an "Alle" tab plus one tab per customer group,
 * a single tabpanel with the product grid, and the per-card actions
 * (remove, add to cart, add to group).
 *
 * Data + request helpers come from window.Wishlist (assets/wishlist.js).
 * Dialogs and group mutations live in window.WishlistGroups
 * (assets/wishlist-groups.js), which is loaded right after this file.
 */
(function () {
  'use strict';

  var MAX_GROUPS = 20;
  var MAX_NAME = 60;
  var PANEL_ID = 'wishlist-panel';
  var TAB_ID_PREFIX = 'wishlist-tab-';
  var ALL_KEY = 'all';

  /* -----------------------------------------------------------------------
     State
     ----------------------------------------------------------------------- */

  var state = {
    ready: false,
    lists: [],        // [{ id: 'gid://...', name: 'Cadeaus' }]
    entries: [],      // [{ productId: '123', listId: 'gid://...'|null, ref: {...} }]
    activeIndex: -1,  // -1 = Alle, otherwise index into state.lists
  };

  var root = null;
  var pending = {};   // in-flight guard keyed by productId + '|' + listId

  /* -----------------------------------------------------------------------
     Shared helpers
     ----------------------------------------------------------------------- */

  function core() {
    return window.Wishlist || {};
  }

  function esc(value) {
    var fn = core().escapeHtml;
    if (fn) return fn(value);
    if (value === null || value === undefined) return '';
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function normalizeId(value) {
    var fn = core().normalizeId;
    return fn ? fn(value) : String(value || '').replace(/\D/g, '');
  }

  function baseStrings() {
    return window.__wishlistStrings || {};
  }

  function groupStrings() {
    return window.__wishlistGroupStrings || {};
  }

  function groups() {
    return window.WishlistGroups || null;
  }

  function hasOwn(obj, key) {
    return Object.prototype.hasOwnProperty.call(obj, key);
  }

  /** Documented backend response body for the group cap, matched exactly */
  var LIST_LIMIT_ERROR = 'Maximum number of wishlist lists reached';

  /**
   * True when a 422 represents the documented "too many lists" cap rather than
   * an ordinary field validation failure. The server string is only ever
   * compared against the documented constant to pick a translated message —
   * it is never rendered.
   */
  function isListLimitError(err) {
    if (!err || err.status !== 422) return false;
    if (err.body && err.body.error === LIST_LIMIT_ERROR) return true;
    if (state.lists.length >= MAX_GROUPS) return true;
    if (err.fieldErrors) {
      for (var key in err.fieldErrors) {
        if (hasOwn(err.fieldErrors, key) && /limit|max/i.test(key)) return true;
      }
    }
    return false;
  }

  /**
   * Map an API error onto translated, customer-facing copy.
   * Server-provided validation prose is never rendered.
   */
  function messageForError(err) {
    var s = groupStrings();
    var generic = s.error_generic || baseStrings().error || '';
    if (!err) return generic;
    if (err.status === 401) return s.error_login || generic;
    if (err.status === 404) return s.error_not_found || generic;
    if (err.status === 429) return s.error_rate_limited || generic;
    if (err.status === 422 && isListLimitError(err)) return s.error_limit || generic;
    return generic;
  }

  /** Whether the API flagged a specific field — drives placement, never copy */
  function hasFieldError(err, field) {
    if (!err || !err.fieldErrors) return false;
    var value = err.fieldErrors[field];
    if (Array.isArray(value)) return value.length > 0;
    return typeof value === 'string' && value.length > 0;
  }

  /* -----------------------------------------------------------------------
     Normalization
     ----------------------------------------------------------------------- */

  function normalizeList(raw) {
    if (!raw || !raw.id) return null;
    var name = '';
    if (raw.name && typeof raw.name === 'object' && typeof raw.name.value === 'string') {
      name = raw.name.value;
    } else if (typeof raw.name === 'string') {
      name = raw.name;
    }
    return { id: String(raw.id), name: name };
  }

  function normalizeLists(rawLists) {
    var out = [];
    if (!Array.isArray(rawLists)) return out;
    rawLists.forEach(function (raw) {
      var list = normalizeList(raw);
      if (list) out.push(list);
    });
    return out;
  }

  function normalizeEntries(rawItems) {
    var out = [];
    if (!Array.isArray(rawItems)) return out;
    rawItems.forEach(function (item) {
      var ref = item && item.product && item.product.reference ? item.product.reference : null;
      if (!ref || !ref.id) return;
      var productId = normalizeId(ref.id);
      if (!productId) return;
      var listValue = item.list && typeof item.list === 'object' ? item.list.value : null;
      out.push({
        productId: productId,
        listId: listValue ? String(listValue) : null,
        ref: ref,
      });
    });
    return out;
  }

  /* -----------------------------------------------------------------------
     Derived state
     ----------------------------------------------------------------------- */

  function uniqueProductIds() {
    var seen = {};
    var ids = [];
    state.entries.forEach(function (entry) {
      if (hasOwn(seen, entry.productId)) return;
      seen[entry.productId] = true;
      ids.push(entry.productId);
    });
    return ids;
  }

  function entriesForList(listId) {
    return state.entries.filter(function (entry) { return entry.listId === listId; });
  }

  function activeList() {
    return state.activeIndex >= 0 ? state.lists[state.activeIndex] : null;
  }

  function listIndexById(listId) {
    for (let i = 0; i < state.lists.length; i++) {
      if (state.lists[i].id === listId) return i;
    }
    return -1;
  }

  function countForList(listId) {
    return entriesForList(listId).length;
  }

  function membershipsFor(productId) {
    var ids = [];
    state.entries.forEach(function (entry) {
      if (entry.productId === productId && entry.listId && ids.indexOf(entry.listId) === -1) {
        ids.push(entry.listId);
      }
    });
    return ids;
  }

  function refFor(productId) {
    for (let i = 0; i < state.entries.length; i++) {
      if (state.entries[i].productId === productId) return state.entries[i].ref;
    }
    return null;
  }

  /** Cards for the current view: Alle dedupes by product, groups keep entry identity */
  function visibleRefs() {
    var list = activeList();
    if (!list) {
      const seen = {};
      const refs = [];
      state.entries.forEach(function (entry) {
        if (hasOwn(seen, entry.productId)) return;
        seen[entry.productId] = true;
        refs.push(entry.ref);
      });
      return refs;
    }
    return entriesForList(list.id).map(function (entry) { return entry.ref; });
  }

  function syncGlobalIds() {
    if (core().setIds) core().setIds(uniqueProductIds());
  }

  /* -----------------------------------------------------------------------
     Pricing: VAT of the selected delivery country via window.Vat (assets/vat.js)
     ----------------------------------------------------------------------- */

  function formatPrice(cents, currencyCode) {
    var symbol = currencyCode === 'EUR' ? '\u20AC' : currencyCode + ' ';
    var abs = Math.abs(cents);
    var decimals = ('0' + (abs % 100)).slice(-2);
    return (cents < 0 ? '-' : '') + symbol + Math.floor(abs / 100) + ',' + decimals;
  }

  /**
   * Reduced rate when the product carries the ReducedVatRate tag, 0% when its
   * first variant is not taxable. Older API responses without tags/taxable
   * fall back to the standard rate of the selected country.
   */
  function calcPrices(amount, currencyCode, ref) {
    var vat = window.Vat;
    if (!vat) return null;
    var variants = ref && ref.variants && Array.isArray(ref.variants.nodes) ? ref.variants.nodes : [];
    var className = vat.rateClass({
      tags: ref ? ref.tags : undefined,
      taxable: variants.length ? variants[0].taxable : undefined,
    });
    var parts = vat.split(vat.toCents(amount), vat.rateBp(className));
    return {
      incl: formatPrice(parts.incl, currencyCode),
      excl: formatPrice(parts.excl, currencyCode),
    };
  }

  /* -----------------------------------------------------------------------
     Markup builders
     ----------------------------------------------------------------------- */

  var ICON_HEART =
    '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>';

  var ICON_CLOSE =
    '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';

  var ICON_FOLDER_PLUS =
    '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 20a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h5l2 3h7a2 2 0 0 1 2 2v3"/><line x1="17" y1="14" x2="17" y2="20"/><line x1="14" y1="17" x2="20" y2="17"/></svg>';

  var ICON_PENCIL =
    '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4z"/></svg>';

  var ICON_TRASH =
    '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>';

  var ICON_IMAGE_PLACEHOLDER =
    '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>';

  function buildCard(ref) {
    var s = baseStrings();
    var g = groupStrings();
    var nid = normalizeId(ref.id);
    var title = ref.title || '';
    var handle = ref.handle || '';
    var safeHandle = esc(handle);
    var productUrl = '/products/' + safeHandle;
    var imgUrl = ref.featuredImage && ref.featuredImage.url ? ref.featuredImage.url : '';
    var imgAlt = ref.featuredImage && ref.featuredImage.altText ? ref.featuredImage.altText : title;
    var priceData = ref.priceRangeV2 && ref.priceRangeV2.minVariantPrice
      ? ref.priceRangeV2.minVariantPrice
      : null;
    var priceHtml = '';
    var prices;

    if (priceData) prices = calcPrices(priceData.amount, priceData.currencyCode, ref);
    if (prices) {
      priceHtml =
        '<div class="wishlist-item__prices">' +
          '<span class="wishlist-item__price-incl">' + esc(prices.incl) + ' <small>incl. BTW</small></span>' +
          '<span class="wishlist-item__price-excl">' + esc(prices.excl) + ' <small>excl. BTW</small></span>' +
        '</div>';
    }

    var imageHtml = imgUrl
      ? '<img class="wishlist-item__image" src="' + esc(imgUrl + '&width=400') + '" alt="' + esc(imgAlt) + '" loading="lazy" width="400" height="400">'
      : '<div class="wishlist-item__image-placeholder">' + ICON_IMAGE_PLACEHOLDER + '</div>';

    return '<li class="wishlist-item" data-wishlist-product="' + esc(nid) + '">' +
      '<div class="wishlist-item__image-wrapper">' +
        '<a href="' + productUrl + '">' + imageHtml + '</a>' +
        '<button type="button" class="wishlist-item__remove" data-wishlist-remove="' + esc(nid) + '"' +
          ' aria-label="' + esc(s.remove_item || '') + '" title="' + esc(s.remove_item || '') + '">' +
          ICON_CLOSE +
        '</button>' +
      '</div>' +
      '<div class="wishlist-item__info">' +
        '<h3 class="wishlist-item__title"><a href="' + productUrl + '">' + esc(title) + '</a></h3>' +
        priceHtml +
        '<div class="wishlist-item__actions">' +
          '<button type="button" class="wishlist-item__cart-btn" data-wishlist-cart="' + safeHandle + '">' +
            esc(s.add_to_cart || '') +
          '</button>' +
          '<button type="button" class="wishlist-item__group-btn" data-wishlist-group="' + esc(nid) + '">' +
            ICON_FOLDER_PLUS +
            '<span>' + esc(g.add_to_group || '') + '</span>' +
          '</button>' +
        '</div>' +
      '</div>' +
    '</li>';
  }

  function buildTabs() {
    var g = groupStrings();
    var html =
      '<button type="button" role="tab" class="wishlist-nav__link' + (state.activeIndex === -1 ? ' is-active' : '') + '"' +
        ' id="' + TAB_ID_PREFIX + ALL_KEY + '" aria-controls="' + PANEL_ID + '"' +
        ' aria-selected="' + (state.activeIndex === -1 ? 'true' : 'false') + '"' +
        ' tabindex="' + (state.activeIndex === -1 ? '0' : '-1') + '" data-wishlist-tab="' + ALL_KEY + '">' +
        '<span class="wishlist-nav__label">' + esc(g.all || '') + '</span>' +
        '<span class="wishlist-nav__count">' + uniqueProductIds().length + '</span>' +
      '</button>';

    state.lists.forEach(function (list, index) {
      var selected = state.activeIndex === index;
      html +=
        '<button type="button" role="tab" class="wishlist-nav__link' + (selected ? ' is-active' : '') + '"' +
          ' id="' + TAB_ID_PREFIX + index + '" aria-controls="' + PANEL_ID + '"' +
          ' aria-selected="' + (selected ? 'true' : 'false') + '"' +
          ' tabindex="' + (selected ? '0' : '-1') + '" data-wishlist-tab="' + index + '"' +
          ' title="' + esc(list.name) + '">' +
          '<span class="wishlist-nav__label">' + esc(list.name) + '</span>' +
          '<span class="wishlist-nav__count">' + countForList(list.id) + '</span>' +
        '</button>';
    });

    return html;
  }

  function buildGroupHeader() {
    var list = activeList();
    if (!list) return '';
    var g = groupStrings();
    return '<div class="wishlist-group__header">' +
        '<h2 class="wishlist-group__title" title="' + esc(list.name) + '">' +
          esc(list.name) +
          ' <span class="wishlist-category__count">' + countForList(list.id) + '</span>' +
        '</h2>' +
        '<div class="wishlist-group__actions">' +
          '<button type="button" class="wishlist-group-action" data-wishlist-rename' +
            ' aria-label="' + esc(g.rename || '') + '" title="' + esc(g.rename || '') + '">' + ICON_PENCIL + '</button>' +
          '<button type="button" class="wishlist-group-action wishlist-group-action--danger" data-wishlist-delete' +
            ' aria-label="' + esc(g.delete_group || '') + '" title="' + esc(g.delete_group || '') + '">' + ICON_TRASH + '</button>' +
        '</div>' +
      '</div>';
  }

  function buildEmptyState() {
    var s = baseStrings();
    var g = groupStrings();
    var list = activeList();

    if (list) {
      return '<div class="wishlist-empty wishlist-empty--group">' +
          '<div class="wishlist-empty__icon">' + ICON_HEART + '</div>' +
          '<h3 class="wishlist-empty__title">' + esc(g.empty_group_title || '') + '</h3>' +
          '<p class="wishlist-empty__text">' + esc(g.empty_group_text || '') + '</p>' +
        '</div>';
    }

    return '<div class="wishlist-empty">' +
        '<div class="wishlist-empty__icon">' + ICON_HEART + '</div>' +
        '<h3 class="wishlist-empty__title">' + esc(s.empty || '') + '</h3>' +
        '<p class="wishlist-empty__text">' + esc(g.empty_all_text || s.empty_description || '') + '</p>' +
        '<a href="/collections/all" class="wishlist-empty__cta">' + esc(s.continue_shopping || '') + '</a>' +
      '</div>';
  }

  function buildPanelContent() {
    var refs = visibleRefs();
    var html = buildGroupHeader();
    if (!refs.length) return html + buildEmptyState();
    var cards = '';
    refs.forEach(function (ref) { cards += buildCard(ref); });
    return html + '<ul class="wishlist-grid">' + cards + '</ul>';
  }

  function activeTabId() {
    return TAB_ID_PREFIX + (state.activeIndex === -1 ? ALL_KEY : state.activeIndex);
  }

  function buildShell() {
    var g = groupStrings();
    return '<div class="wishlist-status" role="status" aria-live="polite" aria-atomic="true"></div>' +
      '<div class="wishlist-layout">' +
        '<aside class="wishlist-sidebar">' +
          '<div class="wishlist-toolbar">' +
            '<div class="wishlist-nav" role="tablist" aria-label="' + esc(g.tablist_label || '') + '">' +
              buildTabs() +
            '</div>' +
            '<button type="button" class="wishlist-nav__create" data-wishlist-create>' +
              esc(g.create || '') +
            '</button>' +
          '</div>' +
        '</aside>' +
        '<div class="wishlist-content">' +
          '<p class="wishlist-page__notice" role="alert" hidden></p>' +
          '<div id="' + PANEL_ID + '" role="tabpanel" tabindex="0"' +
            ' aria-labelledby="' + activeTabId() + '">' +
            buildPanelContent() +
          '</div>' +
        '</div>' +
      '</div>';
  }

  /* -----------------------------------------------------------------------
     Rendering
     ----------------------------------------------------------------------- */

  function renderLoginState() {
    var cfg = core().getConfig ? core().getConfig() : {};
    var s = baseStrings();
    root.removeAttribute('aria-busy');
    root.innerHTML =
      '<div class="wishlist-empty">' +
        '<div class="wishlist-empty__icon">' + ICON_HEART + '</div>' +
        '<h2 class="wishlist-empty__title">' + esc(s.login_required || '') + '</h2>' +
        '<a href="' + esc(cfg.loginUrl || '/account/login') + '" class="wishlist-empty__cta">' +
          esc(s.login_required || '') +
        '</a>' +
      '</div>';
  }

  /**
   * Skeleton shown until the /apps/wishlist round-trip resolves.
   *
   * Every placeholder is a <span>: base.css collapses empty divs
   * (div:empty:not(.bar-progress)), which is why the previous empty-<div>
   * skeleton rendered as a blank page.
   */
  function renderLoading() {
    var card =
      '<li class="wishlist-item wishlist-skeleton">' +
        '<span class="wishlist-skeleton__media"></span>' +
        '<span class="wishlist-skeleton__body">' +
          '<span class="wishlist-skeleton__line wishlist-skeleton__line--lg"></span>' +
          '<span class="wishlist-skeleton__line wishlist-skeleton__line--md"></span>' +
          '<span class="wishlist-skeleton__line wishlist-skeleton__line--sm"></span>' +
          '<span class="wishlist-skeleton__btn"></span>' +
        '</span>' +
      '</li>';

    var tab = '<span class="wishlist-skeleton__tab"></span>';

    root.setAttribute('aria-busy', 'true');
    root.innerHTML =
      '<div class="wishlist-layout">' +
        '<aside class="wishlist-sidebar">' +
          '<span class="wishlist-skeleton__tabs">' + tab.repeat(3) + '</span>' +
        '</aside>' +
        '<div class="wishlist-content">' +
          '<ul class="wishlist-grid">' + card.repeat(8) + '</ul>' +
        '</div>' +
      '</div>';
  }

  /**
   * Rebuild the whole shell. focusTarget: 'tab' | 'rename' | null
   */
  function render(focusTarget) {
    if (!root) return;
    root.removeAttribute('aria-busy');
    root.innerHTML = buildShell();
    bindShell();
    if (focusTarget === 'tab') {
      const tab = root.querySelector('#' + activeTabId());
      if (tab) tab.focus();
    } else if (focusTarget === 'rename') {
      const renameBtn = root.querySelector('[data-wishlist-rename]');
      if (renameBtn) renameBtn.focus();
    }
  }

  function renderPanelOnly() {
    var panel = root.querySelector('#' + PANEL_ID);
    if (!panel) { render(null); return; }
    panel.setAttribute('aria-labelledby', activeTabId());
    panel.innerHTML = buildPanelContent();
    bindPanel(panel);
    syncTabs();
  }

  function syncTabs() {
    var tabs = root.querySelectorAll('[data-wishlist-tab]');
    for (let i = 0; i < tabs.length; i++) {
      const tab = tabs[i];
      const key = tab.getAttribute('data-wishlist-tab');
      const selected = key === ALL_KEY ? state.activeIndex === -1 : String(state.activeIndex) === key;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.setAttribute('tabindex', selected ? '0' : '-1');
      tab.classList.toggle('is-active', selected);
      const countEl = tab.querySelector('.wishlist-nav__count');
      if (countEl) {
        countEl.textContent = key === ALL_KEY
          ? uniqueProductIds().length
          : countForList(state.lists[parseInt(key, 10)].id);
      }
    }
  }

  function updateCounts() {
    syncTabs();
    var list = activeList();
    if (!list) return;
    var headCount = root.querySelector('.wishlist-group__title .wishlist-category__count');
    if (headCount) headCount.textContent = countForList(list.id);
  }

  /* -----------------------------------------------------------------------
     Announcements & notices
     ----------------------------------------------------------------------- */

  function announce(message) {
    if (!root || !message) return;
    var el = root.querySelector('.wishlist-status');
    if (!el) return;
    el.textContent = '';
    window.setTimeout(function () { el.textContent = message; }, 50);
  }

  function showNotice(message) {
    if (!root) return;
    var el = root.querySelector('.wishlist-page__notice');
    if (!el) return;
    if (!message) {
      el.textContent = '';
      el.hidden = true;
      return;
    }
    el.textContent = message;
    el.hidden = false;
  }

  /* -----------------------------------------------------------------------
     Data loading
     ----------------------------------------------------------------------- */

  function load(options) {
    options = options || {};
    var api = core().API || { list: '/apps/wishlist' };
    if (!core().request) return Promise.resolve();

    return core().request(api.list)
      .then(function (data) {
        state.lists = normalizeLists(data && data.lists);
        state.entries = normalizeEntries(data && data.items);
        state.ready = true;

        if (options.keepListId) {
          state.activeIndex = listIndexById(options.keepListId);
        } else if (state.activeIndex >= state.lists.length) {
          state.activeIndex = -1;
        }

        syncGlobalIds();
        render(options.focus || null);
      })
      .catch(function (err) {
        state.ready = true;

        // The session expired (or was never authenticated): the grouped shell is
        // meaningless, so fall back to the login state and drop the stale cache.
        if (err && err.status === 401) {
          state.lists = [];
          state.entries = [];
          state.activeIndex = -1;
          if (core().clearCache) core().clearCache();
          if (core().setIds) core().setIds([]);
          renderLoginState();
          return;
        }

        // Keep whatever state we already had — a failed refetch must not blank the page.
        if (state.activeIndex >= state.lists.length) state.activeIndex = -1;
        render(options.focus || null);
        showNotice(messageForError(err));
        if (core().warnUnexpected) core().warnUnexpected('Wishlist page load failed', err);
      });
  }

  /* -----------------------------------------------------------------------
     Mutations applied by the groups module
     ----------------------------------------------------------------------- */

  function applyCreate(list) {
    if (!list) return;
    state.lists.push(list);
    state.activeIndex = state.lists.length - 1;
    render('tab');
  }

  function applyRename(listId, name) {
    var idx = listIndexById(listId);
    if (idx === -1) return;
    state.lists[idx].name = name;
  }

  function applyDelete() {
    // Deletion cascades entries server-side, so a refetch is the only safe path.
    state.activeIndex = -1;
    return load({ focus: 'tab' });
  }

  /**
   * A mutation hit 404: the list was removed elsewhere. Refetch the truth,
   * fall back to Alle when the stale list was the one on screen, and surface
   * the translated "no longer exists" notice.
   */
  function handleStaleList(listId) {
    var current = activeList();
    var keepListId = current && current.id !== listId ? current.id : '';
    state.activeIndex = -1;
    return load({ keepListId: keepListId, focus: 'tab' }).then(function () {
      showNotice(groupStrings().error_not_found || '');
    });
  }

  function addMembership(productId, listId) {
    var ref = refFor(productId);
    if (!ref) return;
    var exists = state.entries.some(function (entry) {
      return entry.productId === productId && entry.listId === listId;
    });
    if (exists) return;
    state.entries.push({ productId: productId, listId: listId, ref: ref });
    var list = activeList();
    if (list && list.id === listId) {
      renderPanelOnly();
    } else {
      updateCounts();
    }
  }

  function activateByIndex(index, focus) {
    state.activeIndex = index;
    renderPanelOnly();
    if (focus) {
      const tab = root.querySelector('#' + activeTabId());
      if (tab) tab.focus();
    }
  }

  /* -----------------------------------------------------------------------
     Card actions
     ----------------------------------------------------------------------- */

  function removeCard(productId, cardEl) {
    var list = activeList();
    var key = productId + '|' + (list ? list.id : '');
    if (pending[key] || !core().request) return;
    pending[key] = true;

    var api = core().API || {};
    var payload = { product_id: productId };
    if (list) payload.list_id = list.id;

    if (cardEl) {
      cardEl.style.opacity = '0.4';
      cardEl.style.pointerEvents = 'none';
      cardEl.setAttribute('aria-busy', 'true');
    }
    showNotice('');

    core().request(api.remove || '/apps/wishlist/remove', { method: 'POST', data: payload })
      .then(function () {
        if (list) {
          state.entries = state.entries.filter(function (entry) {
            return !(entry.productId === productId && entry.listId === list.id);
          });
          announce(groupStrings().removed_from_group || baseStrings().removed || '');
        } else {
          state.entries = state.entries.filter(function (entry) {
            return entry.productId !== productId;
          });
          announce(baseStrings().removed || '');
        }
        syncGlobalIds();

        if (cardEl && cardEl.parentNode) cardEl.parentNode.removeChild(cardEl);
        var grid = root.querySelector('.wishlist-grid');
        if (!grid || !grid.querySelectorAll('.wishlist-item').length) {
          renderPanelOnly();
        } else {
          updateCounts();
        }
      })
      .catch(function (err) {
        if (cardEl) {
          cardEl.style.opacity = '';
          cardEl.style.pointerEvents = '';
          cardEl.removeAttribute('aria-busy');
        }
        showNotice(messageForError(err));
        if (core().warnUnexpected) core().warnUnexpected('Wishlist remove failed', err);
      })
      .finally(function () {
        delete pending[key];
      });
  }

  function addItemToCart(handle, btn) {
    var cfg = core().getConfig ? core().getConfig() : {};
    var originalText = btn.textContent;
    btn.textContent = '...';
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');

    fetch('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('Product fetch failed');
        return res.json();
      })
      .then(function (product) {
        var variant = null;
        for (let i = 0; i < product.variants.length; i++) {
          if (product.variants[i].available) { variant = product.variants[i]; break; }
        }
        if (!variant) throw new Error('No available variant');

        return fetch(cfg.cartAddUrl || '/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }] }),
        });
      })
      .then(function (res) {
        if (!res.ok) throw new Error('Cart add failed');
        return res.json();
      })
      .then(function () {
        btn.textContent = '\u2713';
        btn.classList.add('wishlist-item__cart-btn--success');
        btn.removeAttribute('aria-busy');
        fetch(cfg.cartUrl || '/cart.js', { credentials: 'same-origin' })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            document.querySelectorAll('.custom-cart-count span[aria-hidden]').forEach(function (el) {
              el.textContent = cart.item_count;
            });
          });
        setTimeout(function () {
          btn.textContent = originalText;
          btn.classList.remove('wishlist-item__cart-btn--success');
          btn.disabled = false;
        }, 2000);
      })
      .catch(function (e) {
        btn.removeAttribute('aria-busy');
        btn.textContent = '!';
        showNotice(baseStrings().error || '');
        if (core().warnUnexpected) core().warnUnexpected('Cart add failed', e);
        setTimeout(function () {
          btn.textContent = originalText;
          btn.disabled = false;
        }, 2000);
      });
  }

  /* -----------------------------------------------------------------------
     Event wiring
     ----------------------------------------------------------------------- */

  function tabButtons() {
    return Array.prototype.slice.call(root.querySelectorAll('[data-wishlist-tab]'));
  }

  function indexForTab(tab) {
    var key = tab.getAttribute('data-wishlist-tab');
    return key === ALL_KEY ? -1 : parseInt(key, 10);
  }

  function bindTablist() {
    var tablist = root.querySelector('.wishlist-nav[role="tablist"]');
    if (!tablist) return;

    tablist.addEventListener('click', function (event) {
      var tab = event.target.closest('[data-wishlist-tab]');
      if (!tab || !tablist.contains(tab)) return;
      event.preventDefault();
      activateByIndex(indexForTab(tab), false);
      tab.focus();
    });

    tablist.addEventListener('keydown', function (event) {
      var key = event.key;
      if (key !== 'ArrowLeft' && key !== 'ArrowRight' && key !== 'Home' && key !== 'End') return;
      var tabs = tabButtons();
      if (!tabs.length) return;
      var current = tabs.indexOf(document.activeElement);
      if (current === -1) return;
      event.preventDefault();

      var next = current;
      if (key === 'ArrowLeft') next = (current - 1 + tabs.length) % tabs.length;
      else if (key === 'ArrowRight') next = (current + 1) % tabs.length;
      else if (key === 'Home') next = 0;
      else if (key === 'End') next = tabs.length - 1;

      activateByIndex(indexForTab(tabs[next]), false);
      var refreshed = tabButtons();
      if (refreshed[next]) refreshed[next].focus();
    });
  }

  function bindPanel(panel) {
    if (panel.getAttribute('data-wishlist-bound') === 'true') return;
    panel.setAttribute('data-wishlist-bound', 'true');
    panel.addEventListener('click', function (event) {
      var removeBtn = event.target.closest('[data-wishlist-remove]');
      if (removeBtn && panel.contains(removeBtn)) {
        event.preventDefault();
        removeCard(removeBtn.getAttribute('data-wishlist-remove'), removeBtn.closest('.wishlist-item'));
        return;
      }

      var cartBtn = event.target.closest('[data-wishlist-cart]');
      if (cartBtn && panel.contains(cartBtn)) {
        event.preventDefault();
        addItemToCart(cartBtn.getAttribute('data-wishlist-cart'), cartBtn);
        return;
      }

      var groupBtn = event.target.closest('[data-wishlist-group]');
      if (groupBtn && panel.contains(groupBtn)) {
        event.preventDefault();
        const mod = groups();
        if (mod && mod.openPicker) {
          mod.openPicker(groupBtn.getAttribute('data-wishlist-group'), groupBtn);
        }
        return;
      }

      var renameBtn = event.target.closest('[data-wishlist-rename]');
      if (renameBtn && panel.contains(renameBtn)) {
        event.preventDefault();
        const renameMod = groups();
        const list = activeList();
        if (renameMod && renameMod.startRename && list) {
          renameMod.startRename(list, renameBtn.closest('.wishlist-group__header'), renameBtn);
        }
        return;
      }

      var deleteBtn = event.target.closest('[data-wishlist-delete]');
      if (deleteBtn && panel.contains(deleteBtn)) {
        event.preventDefault();
        const deleteMod = groups();
        const target = activeList();
        if (deleteMod && deleteMod.openDelete && target) {
          deleteMod.openDelete(target, deleteBtn);
        }
      }
    });
  }

  function bindShell() {
    bindTablist();

    var createBtn = root.querySelector('[data-wishlist-create]');
    if (createBtn) {
      createBtn.addEventListener('click', function (event) {
        event.preventDefault();
        var mod = groups();
        if (mod && mod.openCreate) mod.openCreate(createBtn);
      });
    }

    var panel = root.querySelector('#' + PANEL_ID);
    if (panel) bindPanel(panel);
  }

  /* -----------------------------------------------------------------------
     Init
     ----------------------------------------------------------------------- */

  function init() {
    root = document.querySelector('[data-wishlist-page]');
    if (!root) return;

    if (!core().isLoggedIn || !core().isLoggedIn()) {
      renderLoginState();
      return;
    }

    renderLoading();
    load();
  }

  window.WishlistPage = {
    init: init,
    MAX_GROUPS: MAX_GROUPS,
    MAX_NAME: MAX_NAME,
    getLists: function () { return state.lists.slice(); },
    getActiveList: activeList,
    listCount: function () { return state.lists.length; },
    membershipsFor: membershipsFor,
    normalizeList: normalizeList,
    messageForError: messageForError,
    hasFieldError: hasFieldError,
    announce: announce,
    notify: showNotice,
    escape: esc,
    strings: groupStrings,
    baseStrings: baseStrings,
    reload: load,
    applyCreate: applyCreate,
    applyRename: applyRename,
    applyDelete: applyDelete,
    handleStaleList: handleStaleList,
    addMembership: addMembership,
    render: render,
    getRoot: function () { return root; },
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    window.setTimeout(init, 0);
  }
})();
